import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthContext';
import { TenantProvider } from './auth/TenantContext';
import { ToastProvider } from './ui/Toast';
import { router } from './routes/router';
import { FeaturesProvider } from './features/FeaturesProvider';
import { queryClient } from './lib/queryClient';
import { DirectionProvider } from './lib/direction';
import { ThemeProvider } from './providers/ThemeProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CommandPaletteProvider } from './contexts/CommandPaletteContext';
import { HelmetProvider } from 'react-helmet-async';

export default function App() {
  // Global AbortError suppression for Firestore SDK
  // AbortErrors occur when Firestore SDK aborts in-flight HTTP requests during
  // component unmount or StrictMode cycles - this is expected and safe behavior
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Check if this is an AbortError from Firestore
      if (event.reason?.name === 'AbortError') {
        // Prevent console error output
        event.preventDefault();

        // Silently suppress - this is expected behavior during React StrictMode cleanup
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="system">
            <DirectionProvider>
              <ToastProvider>
                <AuthProvider>
                  <TenantProvider>
                    <CommandPaletteProvider>
                      <FeaturesProvider>
                        <RouterProvider
                          router={router}
                          future={{ v7_startTransition: true }}
                        />
                      </FeaturesProvider>
                    </CommandPaletteProvider>
                  </TenantProvider>
                </AuthProvider>
              </ToastProvider>
            </DirectionProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
